/**
 * Retention below project default — Step 5 review + ARIA rail alerts.
 */

export const FALLBACK_DEFAULT_RETAINAGE = 5

export function projectDefaultRetainage(raw: number | string | null | undefined): number {
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''))
  return Number.isFinite(n) ? n : FALLBACK_DEFAULT_RETAINAGE
}

export type RetentionDefaultSource = 'project' | 'company' | 'fallback'

export function resolveRetentionDefault(
  projectRaw: number | string | null | undefined,
  companyRaw?: number | string | null | undefined,
): { pct: number; source: RetentionDefaultSource } {
  const p = typeof projectRaw === 'number' ? projectRaw : parseFloat(String(projectRaw ?? ''))
  if (Number.isFinite(p)) return { pct: p, source: 'project' }
  const c = typeof companyRaw === 'number' ? companyRaw : parseFloat(String(companyRaw ?? ''))
  if (Number.isFinite(c)) return { pct: c, source: 'company' }
  return { pct: FALLBACK_DEFAULT_RETAINAGE, source: 'fallback' }
}

export interface RetentionLineLike {
  retainage_pct: string | number
  item_id?: string | null
  this_pct?: string | number | null
}

export function isCoLine(itemId?: string | null): boolean {
  return /^CO-/i.test(String(itemId || '').trim())
}

/** Base lines billing this period only; omit this_pct to include (legacy callers). */
export function lineHasThisPeriodBilling(line: RetentionLineLike): boolean {
  if (line.this_pct == null || line.this_pct === '') return true
  return (parseFloat(String(line.this_pct)) || 0) > 1e-9
}

export function countLinesBelowDefault(
  lines: RetentionLineLike[] | undefined,
  projectDefault: number | string | null | undefined,
): number {
  const raw = typeof projectDefault === 'number' ? projectDefault : parseFloat(String(projectDefault ?? ''))
  if (Number.isFinite(raw) && raw <= 0) return 0
  const floor = projectDefaultRetainage(projectDefault)
  if (floor <= 0) return 0
  return (lines ?? []).filter((l) => {
    if (isCoLine(l.item_id)) return false
    if (!lineHasThisPeriodBilling(l)) return false
    const ret = parseFloat(String(l.retainage_pct)) || 0
    return ret < floor - 1e-9
  }).length
}

export function retentionNoticeMessage(
  count: number,
  defaultPct: number | string | null | undefined,
  source: RetentionDefaultSource = 'project',
): string {
  const d = projectDefaultRetainage(defaultPct)
  const noun = count === 1 ? 'line holds' : 'lines hold'
  if (source === 'company' || source === 'fallback') {
    return `No project retention set; using company default of ${d}%. ${count} ${noun} less.`
  }
  return `This project's default retention is ${d}%. ${count} ${noun} less.`
}

export function isRetentionNoticeDismissed(detail: { retention_notice_dismissed_at?: string | null } | null | undefined): boolean {
  return Boolean(detail?.retention_notice_dismissed_at)
}
